import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Copy, Gift, Users, Play, X, Bell, BellOff, Ticket, Lock, Loader2, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { subscribePush, unsubscribePush, getCurrentSubscription, pushSupported } from "../lib/push";
import Navbar from "../components/Navbar";
import { useAuth } from "../lib/auth";

export default function Account() {
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const [polling, setPolling] = useState(false);
  const polledRef = useRef(false);
  const [ref, setRef] = useState(null);
  const [parties, setParties] = useState([]);
  const [pushOn, setPushOn] = useState(false);
  const [breakdown, setBreakdown] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [giftsSent, setGiftsSent] = useState([]);
  const [giftsReceived, setGiftsReceived] = useState([]);
  const loadTickets = () => { api.get("/gcr/tickets/mine").then((r) => setTickets(r.data || [])).catch(() => setTickets([])); };
  const loadGifts = () => {
    api.get("/gcr/tickets/gifts/sent").then((r) => setGiftsSent(r.data || [])).catch(() => setGiftsSent([]));
    api.get("/gcr/tickets/gifts/received").then((r) => setGiftsReceived(r.data || [])).catch(() => setGiftsReceived([]));
  };
  useEffect(() => { loadTickets(); loadGifts(); }, []);

  const loadParties = () => {
    api.get("/watchparty/mine").then((r) => setParties(r.data || [])).catch(() => setParties([]));
  };
  useEffect(() => {
    loadParties();
    const t = setInterval(loadParties, 8000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    if (!pushSupported()) return;
    getCurrentSubscription().then((sub) => setPushOn(!!sub));
  }, []);

  const togglePush = async () => {
    try {
      if (pushOn) { await unsubscribePush(); setPushOn(false); toast.success("Notifications off"); }
      else { await subscribePush(); setPushOn(true); toast.success("Notifications enabled"); }
    } catch (e) { toast.error(e.message || "Could not update"); }
  };

  const sendTest = async () => {
    try {
      const { data } = await api.post("/push/test");
      toast.success(`Sent ${data.sent}/${data.total} test notifications`);
    } catch (e) { toast.error("Test failed — enable notifications first"); }
  };

  // ---- Change password ----
  const [pwCurrent, setPwCurrent] = useState("");
  const [pwNew, setPwNew] = useState("");
  const [pwConfirm, setPwConfirm] = useState("");
  const [pwShow, setPwShow] = useState(false);
  const [pwSaving, setPwSaving] = useState(false);
  const changePassword = async (e) => {
    e.preventDefault();
    if (pwNew.length < 8) { toast.error("New password must be at least 8 characters"); return; }
    if (pwNew !== pwConfirm) { toast.error("New password and confirmation don't match"); return; }
    if (pwNew === pwCurrent) { toast.error("New password must differ from current one"); return; }
    setPwSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: pwCurrent, new_password: pwNew });
      toast.success("Password updated. Use your new password next time you sign in.");
      setPwCurrent(""); setPwNew(""); setPwConfirm("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update password");
    } finally { setPwSaving(false); }
  };

  // Stripe checkout polling
  useEffect(() => {
    const sid = sp.get("session_id");
    if (!sid || polledRef.current) return;
    polledRef.current = true;
    let attempts = 0;
    setPolling(true);
    const tick = async () => {
      attempts += 1;
      try {
        const { data } = await api.get(`/subscriptions/checkout/status/${sid}`);
        if (data.payment_status === "paid") {
          await refresh();
          toast.success(`${data.plan_id?.toUpperCase()} subscription activated!`);
          setPolling(false);
          window.history.replaceState(null, "", "/account");
          return;
        }
        if (data.status === "expired" || attempts >= 10) {
          toast.error("Payment not completed");
          setPolling(false);
          return;
        }
        setTimeout(tick, 2000);
      } catch {
        if (attempts >= 10) { setPolling(false); return; }
        setTimeout(tick, 2000);
      }
    };
    tick();
  }, [sp, refresh]);

  // Stripe ticket-gift polling (buyer returns from Stripe after gifting)
  const giftPolledRef = useRef(false);
  useEffect(() => {
    const sid = sp.get("gift_session_id");
    if (!sid || giftPolledRef.current) return;
    giftPolledRef.current = true;
    let attempts = 0;
    const tick = async () => {
      attempts += 1;
      try {
        const { data } = await api.get(`/gcr/tickets/status/${sid}`);
        if (data.status === "paid") {
          toast.success(`Gift sent to ${data.recipient_email}! 🎁`);
          loadGifts();
          window.history.replaceState(null, "", "/account");
          return;
        }
        if (data.status === "expired" || attempts >= 10) {
          toast.error("Gift checkout did not complete");
          window.history.replaceState(null, "", "/account");
          return;
        }
        setTimeout(tick, 2000);
      } catch {
        if (attempts >= 10) return;
        setTimeout(tick, 2000);
      }
    };
    tick();
  }, [sp]);

  useEffect(() => {
    api.get("/referrals/me").then((r) => setRef(r.data)).catch(() => {});
    api.get("/referrals/breakdown").then((r) => setBreakdown(r.data || [])).catch(() => setBreakdown([]));
  }, []);

  if (!user) return null;

  const buildShareUrl = (extras = {}) => {
    if (!ref?.code) return "";
    const base = `${window.location.origin}/signup`;
    const params = new URLSearchParams({ ref: ref.code, ...extras });
    return `${base}?${params.toString()}`;
  };
  const referralUrl = buildShareUrl();
  const copy = async (text, msg) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(msg || "Copied!");
    } catch { toast.error("Copy failed"); }
  };

  const cancel = async () => {
    if (!confirm("Cancel subscription?")) return;
    await api.post("/subscriptions/cancel");
    await refresh();
    toast.success("Subscription canceled");
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <Navbar />
      <main className="pt-28 px-6 lg:px-12 max-w-3xl mx-auto pb-20">
        <h1 className="text-4xl font-bold mb-8">Account</h1>

        {polling && (
          <div data-testid="payment-polling" className="mb-6 bg-[#1a0808] border border-[#E50914]/40 rounded-lg p-4 flex items-center gap-3">
            <div className="w-5 h-5 border-2 border-[#E50914] border-t-transparent rounded-full animate-spin" />
            <span className="text-sm">Verifying payment with Stripe…</span>
          </div>
        )}

        <div className="bg-[#141414] border border-white/5 rounded-lg p-6 mb-6">
          <h2 className="text-sm uppercase tracking-widest text-gray-500 mb-4">Membership</h2>
          <div className="flex justify-between py-2 border-b border-white/5">
            <span className="text-gray-400">Name</span><span>{user.name}</span>
          </div>
          <div className="flex justify-between py-2 border-b border-white/5">
            <span className="text-gray-400">Email</span><span>{user.email}</span>
          </div>
          <div className="flex justify-between py-2 border-b border-white/5">
            <span className="text-gray-400">Plan</span>
            <span className="capitalize">{user.subscription_tier || "None"} — {user.subscription_status}</span>
          </div>
          {user.free_months_balance > 0 && (
            <div className="flex justify-between py-2 border-b border-white/5">
              <span className="text-gray-400">Free months earned</span>
              <span className="text-green-400 font-semibold">{user.free_months_balance} month{user.free_months_balance > 1 ? "s" : ""}</span>
            </div>
          )}
        </div>

        <div className="bg-[#141414] border border-white/5 rounded-lg p-6 mb-6">
          <h2 className="text-sm uppercase tracking-widest text-gray-500 mb-4">Plan Details</h2>
          {user.subscription_status === "active" ? (
            <button data-testid="cancel-sub-btn" onClick={cancel} className="btn-glass px-6 py-3 rounded font-semibold">Cancel Subscription</button>
          ) : (
            <Link to="/pricing" className="btn-red inline-block px-6 py-3 rounded font-semibold">Choose a plan</Link>
          )}
        </div>

        {/* Notifications */}
        {pushSupported() && (
          <div data-testid="notifications-section" className="bg-[#0f0f0f] border border-white/5 rounded-lg p-6 mb-6 flex items-center gap-4">
            <div className={`p-2.5 rounded-lg ${pushOn ? "bg-[#E50914]/20" : "bg-white/5"}`}>
              {pushOn ? <Bell className="w-5 h-5 text-[#E50914]" /> : <BellOff className="w-5 h-5 text-gray-500" />}
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-semibold">Push notifications</div>
              <div className="text-xs text-gray-500 mt-0.5">
                {pushOn ? "On — you'll get reminders and new-release alerts." : "Off — turn on to set GCR Theaters reminders."}
              </div>
            </div>
            {pushOn && (
              <button data-testid="push-test-btn" onClick={sendTest} className="btn-glass px-3 py-1.5 rounded text-xs">Send test</button>
            )}
            <button data-testid="push-toggle-btn" onClick={togglePush} className="btn-red px-4 py-2 rounded text-sm font-semibold">
              {pushOn ? "Disable" : "Enable"}
            </button>
          </div>
        )}

        {/* Change password (only for email/password accounts) */}
        <div data-testid="password-section" className="bg-[#0f0f0f] border border-white/5 rounded-lg p-6 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2.5 rounded-lg bg-white/5">
              <Lock className="w-5 h-5 text-[#E50914]" />
            </div>
            <div>
              <div className="font-semibold">Change password</div>
              <div className="text-xs text-gray-500 mt-0.5">
                Rotate your admin / account password. Uses bcrypt — the server never sees it in plaintext after hashing.
              </div>
            </div>
          </div>
          <form onSubmit={changePassword} className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="relative">
              <input
                data-testid="pw-current"
                type={pwShow ? "text" : "password"}
                required
                value={pwCurrent}
                onChange={(e) => setPwCurrent(e.target.value)}
                placeholder="Current password"
                autoComplete="current-password"
                className="w-full bg-black/60 border border-white/15 focus:border-[#E50914] outline-none rounded px-3 py-2.5 text-sm"
              />
            </div>
            <div className="relative">
              <input
                data-testid="pw-new"
                type={pwShow ? "text" : "password"}
                required
                minLength={8}
                value={pwNew}
                onChange={(e) => setPwNew(e.target.value)}
                placeholder="New password (min 8 chars)"
                autoComplete="new-password"
                className="w-full bg-black/60 border border-white/15 focus:border-[#E50914] outline-none rounded px-3 py-2.5 text-sm"
              />
            </div>
            <div className="relative">
              <input
                data-testid="pw-confirm"
                type={pwShow ? "text" : "password"}
                required
                minLength={8}
                value={pwConfirm}
                onChange={(e) => setPwConfirm(e.target.value)}
                placeholder="Confirm new password"
                autoComplete="new-password"
                className="w-full bg-black/60 border border-white/15 focus:border-[#E50914] outline-none rounded px-3 py-2.5 text-sm"
              />
            </div>
            <div className="md:col-span-3 flex items-center justify-between flex-wrap gap-3 mt-1">
              <button
                type="button"
                onClick={() => setPwShow((v) => !v)}
                className="text-xs text-gray-400 hover:text-white flex items-center gap-1.5"
                data-testid="pw-toggle-visibility"
              >
                {pwShow ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                {pwShow ? "Hide" : "Show"} passwords
              </button>
              <button
                data-testid="pw-save-btn"
                type="submit"
                disabled={pwSaving || !pwCurrent || !pwNew || !pwConfirm}
                className="btn-red px-5 py-2.5 rounded text-sm font-semibold flex items-center gap-2 disabled:opacity-50"
              >
                {pwSaving ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving…</> : <><Lock className="w-4 h-4" /> Update password</>}
              </button>
            </div>
          </form>
        </div>

        {/* My Tickets (GCR premium events) */}
        {tickets.length > 0 && (
          <div data-testid="tickets-section" className="bg-gradient-to-br from-[#2a0a0a]/40 to-[#0f0f0f] border border-[#E50914]/30 rounded-lg p-6 mb-6">
            <div className="flex items-center gap-2 mb-4">
              <Ticket className="w-5 h-5 text-[#E50914]" />
              <h2 className="text-lg font-semibold">My Tickets</h2>
              <span className="ml-auto text-xs text-[#E50914]/80 uppercase tracking-widest">
                {tickets.filter(t => t.status === "paid").length} active
              </span>
            </div>
            <div className="space-y-2">
              {tickets.map((t) => (
                <div key={t.ticket_id} data-testid={`ticket-${t.ticket_id}`} className="flex items-center gap-3 bg-black/40 border border-white/5 rounded p-3 text-sm">
                  {t.poster && <img src={t.poster} alt="" className="w-10 h-14 object-cover rounded flex-shrink-0" />}
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold truncate">{t.title || t.video_id}</div>
                    <div className="text-xs text-gray-500">
                      {t.channel_name} · {t.schedule?.start_time || ""} · ${t.amount?.toFixed(2)} {t.currency?.toUpperCase()}
                    </div>
                    {t.status === "paid" && t.access_code && (
                      <div
                        data-testid={`ticket-code-${t.ticket_id}`}
                        className="inline-flex items-center gap-1.5 mt-1.5 text-[10px] uppercase tracking-widest text-amber-400"
                        title="Tap to copy — share with anyone you want to grant this seat to"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigator.clipboard?.writeText(t.access_code).then(
                            () => toast.success("Code copied"),
                            () => toast.error("Couldn't copy")
                          );
                        }}
                        role="button"
                      >
                        <Ticket className="w-2.5 h-2.5" />
                        <span className="font-mono">{t.access_code}</span>
                        <Copy className="w-2.5 h-2.5 opacity-60" />
                      </div>
                    )}
                  </div>
                  <span className={`text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded ${
                    t.status === "paid" ? "bg-green-500/15 text-green-400 border border-green-500/40" :
                    t.status === "refunded" ? "bg-gray-500/15 text-gray-400 border border-gray-500/30" :
                    "bg-yellow-500/15 text-yellow-400 border border-yellow-500/40"
                  }`}>{t.status}</span>
                  {t.status === "paid" && (
                    <>
                      <button onClick={() => nav(`/live/${t.channel_slug}`)} className="btn-red px-3 py-1.5 rounded text-xs flex items-center gap-1">
                        <Play className="w-3 h-3 fill-current" /> Watch
                      </button>
                      <button
                        data-testid={`ticket-refund-${t.ticket_id}`}
                        onClick={async () => {
                          if (!confirm("Refund this ticket? (Only available before show starts)")) return;
                          try { await api.post(`/gcr/tickets/${t.ticket_id}/refund`); toast.success("Refund processed"); loadTickets(); }
                          catch (e) { toast.error(e?.response?.data?.detail || "Refund failed"); }
                        }}
                        className="btn-glass px-3 py-1.5 rounded text-xs">Refund</button>
                    </>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Gifts Received */}
        {giftsReceived.length > 0 && (
          <div data-testid="gifts-received-section" className="bg-gradient-to-br from-pink-900/20 to-[#0f0f0f] border border-pink-500/30 rounded-lg p-6 mb-6">
            <div className="flex items-center gap-2 mb-4">
              <Gift className="w-5 h-5 text-pink-300" />
              <h2 className="text-lg font-semibold">Gifts for You</h2>
              <span className="ml-auto text-xs text-pink-300/80 uppercase tracking-widest">
                {giftsReceived.length} unlocked
              </span>
            </div>
            <div className="space-y-2">
              {giftsReceived.map((t) => (
                <div key={t.ticket_id} data-testid={`gift-received-${t.ticket_id}`} className="flex items-center gap-3 bg-black/40 border border-pink-500/20 rounded p-3 text-sm">
                  {t.poster && <img src={t.poster} alt="" className="w-10 h-14 object-cover rounded flex-shrink-0" />}
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold truncate">{t.title || t.video_id}</div>
                    <div className="text-xs text-gray-500 truncate">
                      From <span className="text-pink-300">{t.gifted_by_name || "a friend"}</span> · {t.channel_name} · {t.schedule?.start_time || ""}
                    </div>
                    {t.gift_message && (
                      <div className="text-xs text-pink-200/80 italic mt-1 truncate" title={t.gift_message}>"{t.gift_message}"</div>
                    )}
                  </div>
                  <button onClick={() => nav(`/live/${t.channel_slug}`)} className="btn-red px-3 py-1.5 rounded text-xs flex items-center gap-1">
                    <Play className="w-3 h-3 fill-current" /> Watch
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Gifts Sent */}
        {giftsSent.length > 0 && (
          <div data-testid="gifts-sent-section" className="bg-[#0f0f0f] border border-white/5 rounded-lg p-6 mb-6">
            <div className="flex items-center gap-2 mb-4">
              <Gift className="w-5 h-5 text-gray-400" />
              <h2 className="text-lg font-semibold">Gifts You've Sent</h2>
              <span className="ml-auto text-xs text-gray-500 uppercase tracking-widest">
                {giftsSent.filter(g => g.status === "paid").length} delivered
              </span>
            </div>
            <div className="space-y-2">
              {giftsSent.map((t) => (
                <div key={t.ticket_id} data-testid={`gift-sent-${t.ticket_id}`} className="flex items-center gap-3 bg-black/40 border border-white/5 rounded p-3 text-sm">
                  {t.poster && <img src={t.poster} alt="" className="w-10 h-14 object-cover rounded flex-shrink-0" />}
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold truncate">{t.title || t.video_id}</div>
                    <div className="text-xs text-gray-500 truncate">
                      To <span className="text-white">{t.recipient_email}</span> · ${t.amount?.toFixed(2)} {t.currency?.toUpperCase()}
                    </div>
                  </div>
                  <span className={`text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded ${
                    t.status === "paid" && t.user_id ? "bg-green-500/15 text-green-400 border border-green-500/40" :
                    t.status === "paid" ? "bg-amber-500/15 text-amber-400 border border-amber-500/40" :
                    t.status === "refunded" ? "bg-gray-500/15 text-gray-400 border border-gray-500/30" :
                    "bg-yellow-500/15 text-yellow-400 border border-yellow-500/40"
                  }`}>
                    {t.status === "paid" ? (t.user_id ? "claimed" : "awaiting signup") : t.status}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Currently Hosting Watch Parties */}
        {parties.length > 0 && (
          <div data-testid="hosting-section" className="bg-gradient-to-br from-cyan-900/20 to-[#0f0f0f] border border-cyan-500/30 rounded-lg p-6 mb-6">
            <div className="flex items-center gap-2 mb-4">
              <Users className="w-5 h-5 text-cyan-300" />
              <h2 className="text-lg font-semibold">Currently Hosting</h2>
              <span className="ml-auto text-xs text-cyan-300/80 uppercase tracking-widest">
                {parties.length} active {parties.length === 1 ? "party" : "parties"}
              </span>
            </div>
            <div className="space-y-3">
              {parties.map((p) => (
                <div key={p.room_id} data-testid={`hosting-${p.room_id}`} className="flex items-center gap-4 bg-black/40 border border-white/5 rounded p-3">
                  {p.poster && <img src={p.poster} alt="" className="w-12 h-16 object-cover rounded flex-shrink-0" />}
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold truncate">{p.title}</div>
                    <div className="text-xs text-cyan-300 mt-0.5">
                      <span className="inline-flex items-center gap-1"><Users className="w-3 h-3" /> {p.viewers} watching</span>
                    </div>
                  </div>
                  <button
                    data-testid={`hosting-resume-${p.room_id}`}
                    onClick={() => nav(`/watch/${p.video_id}?party=${p.room_id}`)}
                    className="btn-red px-4 py-2 rounded text-sm font-semibold flex items-center gap-1.5"
                  >
                    <Play className="w-4 h-4 fill-current" /> Resume
                  </button>
                  <button
                    data-testid={`hosting-end-${p.room_id}`}
                    onClick={async () => {
                      if (!confirm("End this Watch Party? All viewers will be disconnected.")) return;
                      try {
                        await api.delete(`/watchparty/${p.room_id}`);
                        toast.success("Watch Party ended");
                        loadParties();
                      } catch { toast.error("Could not end party"); }
                    }}
                    className="btn-glass px-3 py-2 rounded text-sm flex items-center gap-1.5"
                    aria-label="end party"
                  >
                    <X className="w-4 h-4" /> End
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Refer-a-friend */}
        <div data-testid="referral-section" className="bg-gradient-to-br from-[#1a0808] to-[#0f0f0f] border border-[#E50914]/30 rounded-lg p-6">
          <div className="flex items-center gap-2 mb-3">
            <Gift className="w-5 h-5 text-[#E50914]" />
            <h2 className="text-lg font-semibold">Refer a friend, get 1 month free</h2>
          </div>
          <p className="text-sm text-gray-300 mb-4">
            When your friend subscribes, both of you get a free month credited to your account.
          </p>
          {ref && (
            <>
              <div className="grid grid-cols-3 gap-3 mb-5">
                <Stat label="Your code" value={ref.code} mono />
                <Stat label="Pending" value={ref.pending} />
                <Stat label="Earned" value={ref.completed} icon={<Users className="w-4 h-4" />} />
              </div>
              <div className="space-y-2">
                <div className="flex gap-2">
                  <input readOnly value={referralUrl} className="input-dark flex-1 text-xs font-mono" data-testid="referral-url" />
                  <button data-testid="referral-copy-btn" onClick={() => copy(referralUrl, "Referral link copied!")} className="btn-red px-4 py-2 rounded font-semibold flex items-center gap-2">
                    <Copy className="w-4 h-4" /> Copy link
                  </button>
                </div>
                {/* UTM preset share buttons */}
                <div className="flex flex-wrap gap-2 pt-2">
                  <span className="text-[11px] text-gray-500 uppercase tracking-widest self-center mr-1">Share to:</span>
                  {[
                    { k: "twitter", label: "Twitter/X", url: (u) => `https://twitter.com/intent/tweet?text=${encodeURIComponent("Try CineCinema — 24/7 channels + movies")}&url=${encodeURIComponent(u)}` },
                    { k: "whatsapp", label: "WhatsApp", url: (u) => `https://wa.me/?text=${encodeURIComponent("Watch movies & live channels on CineCinema " + u)}` },
                    { k: "facebook", label: "Facebook", url: (u) => `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(u)}` },
                    { k: "telegram", label: "Telegram", url: (u) => `https://t.me/share/url?url=${encodeURIComponent(u)}&text=${encodeURIComponent("CineCinema")}` },
                    { k: "email", label: "Email", url: (u) => `mailto:?subject=${encodeURIComponent("Join me on CineCinema")}&body=${encodeURIComponent("Watch movies + 24/7 live channels together: " + u)}` },
                    { k: "copy", label: "Copy w/ UTM" },
                  ].map((s) => {
                    const u = buildShareUrl({ utm_source: s.k, utm_medium: "share", utm_campaign: "referral" });
                    return (
                      <button
                        key={s.k}
                        data-testid={`share-${s.k}`}
                        onClick={() => { if (s.url) window.open(s.url(u), "_blank"); else copy(u, `${s.label} link copied!`); }}
                        className="btn-glass px-3 py-1.5 rounded text-xs"
                      >{s.label}</button>
                    );
                  })}
                </div>
                <p className="text-xs text-gray-500">Each channel gets its own UTM tag so you can see where your signups come from.</p>
              </div>

              {/* Conversion-by-channel breakdown */}
              {breakdown.length > 0 && (
                <div data-testid="attribution-breakdown" className="mt-6 border-t border-white/5 pt-5">
                  <div className="text-[11px] uppercase tracking-widest text-gray-500 mb-3">Conversion by channel</div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="text-[10px] text-gray-500 uppercase tracking-widest">
                        <tr>
                          <th className="text-left py-2">Source</th>
                          <th className="text-left py-2">Medium</th>
                          <th className="text-right py-2">Clicked</th>
                          <th className="text-right py-2">Subscribed</th>
                          <th className="text-right py-2">Rate</th>
                        </tr>
                      </thead>
                      <tbody>
                        {breakdown.map((r, i) => (
                          <tr key={i} className="border-t border-white/5">
                            <td className="py-2 font-mono text-xs">{r.source}</td>
                            <td className="py-2 text-gray-400">{r.medium}</td>
                            <td className="py-2 text-right">{r.total}</td>
                            <td className="py-2 text-right text-green-400 font-semibold">{r.completed}</td>
                            <td className="py-2 text-right"><span className={`${r.conversion_rate >= 50 ? "text-green-400" : r.conversion_rate > 0 ? "text-yellow-400" : "text-gray-500"}`}>{r.conversion_rate}%</span></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </main>
    </div>
  );
}

function Stat({ label, value, icon, mono }) {
  return (
    <div className="bg-black/40 border border-white/5 rounded p-3">
      <div className="text-[10px] uppercase tracking-widest text-gray-500 mb-1 flex items-center gap-1">
        {icon} {label}
      </div>
      <div className={`text-base font-semibold ${mono ? "font-mono" : ""}`}>{value}</div>
    </div>
  );
}
